import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AttributeFilter, DashboardPresetFilters } from '@/entities/dashboards';
import { type DashboardPresetItem, useDashboardPresets } from '@/entities/settings';
import type { DashboardFiltersState } from '../ui/QualityFilters';
import { getDefaultPanelFilters } from './dashboardFilterDefaults';

/**
 * Задержка записи фильтров серверного дашборда тем, кто его правит: запись уходит на сервер, а поля
 * панели (период, текстовый атрибутный фильтр) отдают значение на каждое нажатие.
 */
const SERVER_WRITE_DELAY_MS = 500;

/** Фильтры с консультантами фильтра по атрибуту; без них — фильтра консультанта на панели нет. */
const withEmployeeIds = (filters: DashboardPresetFilters, employeeIds: number[] | undefined): DashboardPresetFilters =>
  employeeIds === undefined ? filters : { ...filters, employeeIds };

/** Смена фильтров серверного дашборда, ещё не записанная на сервер. */
type PendingFilters = {
  presetId: string;
  filters: DashboardPresetFilters;
};

/** Фильтры стандартного дашборда, сохраняемые за пользователем, и их обработчики. */
type StandardPanelFilters = {
  filters: DashboardFiltersState;
  activeAttributeFilters: AttributeFilter[];
  handleChangeFilters: (next: DashboardFiltersState) => void;
  handleChangeAttributeFilters: (next: AttributeFilter[]) => void;
  handleResetFilters: () => void;
};

/**
 * Фильтры панели страницы дашбордов. Пока применён пользовательский дашборд, период и атрибутные
 * фильтры — его собственные: панель показывает и меняет их, а фильтры стандартного дашборда,
 * сохранённые за пользователем, не трогает. Отдельного консультанта и режима тепловой карты в панели
 * пользовательского дашборда нет: консультант стандартного в фильтрах страницы пуст, а свои
 * консультанты дашборда добавляются фильтром по атрибуту; режим остаётся стандартного.
 *
 * Смену фильтров совместного дашборда у владельца записывает сервер, поэтому она уходит с задержкой,
 * а до записи панель показывает уже выбранное. Отказ сервера возвращает панель к записанным фильтрам.
 * У системного дашборда смена, даже у автора, остаётся за пользователем и на сервер сразу не идёт.
 *
 * @param preset - Применённый пользовательский дашборд; `null` — показан стандартный
 * @param standard - Фильтры стандартного дашборда
 */
export const useDashboardPanelFilters = (preset: DashboardPresetItem | null, standard: StandardPanelFilters) => {
  const { activeFilters, setActiveFilters, resetActiveFilters } = useDashboardPresets();

  const [pending, setPending] = useState<PendingFilters | null>(null);
  // Отложенная запись: её доводит до сервера и уход с дашборда раньше срока
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduledWriteRef = useRef<(() => Promise<void>) | null>(null);

  const presetId = preset?.id ?? null;
  // Системный дашборд смену фильтров на сервер сразу не пишет — даже у автора она остаётся за ним
  const isServerWrite = Boolean(preset?.isShared && preset.isEditable);

  // Дашборд без своих фильтров показывает умолчание; держим его одним значением, чтобы панель и
  // диаграммы не получали новый период на каждой отрисовке
  const defaults = useMemo(() => getDefaultPanelFilters(), []);

  /** Записывает отложенную смену сейчас; обещание — до ответа сервера. */
  const flushScheduledWrite = useCallback((): Promise<void> => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    const write = scheduledWriteRef.current;
    scheduledWriteRef.current = null;
    return write ? write() : Promise.resolve();
  }, []);

  // Уход с дашборда — на другой, на стандартный или со страницы — записывает отложенную смену в тот
  // дашборд, где её сделали: запись помнит дашборд, применённый в момент смены
  useEffect(() => {
    // Без применённого дашборда отложенной записи не бывает
    if (!presetId) return;
    return () => {
      void flushScheduledWrite();
    };
  }, [presetId, flushScheduledWrite]);

  const presetFilters = useMemo<DashboardPresetFilters | null>(() => {
    if (!presetId) return null;
    if (pending?.presetId === presetId) return pending.filters;
    return activeFilters ?? defaults;
  }, [presetId, pending, activeFilters, defaults]);

  /**
   * Показывает смену сразу; у серверного дашборда, который пользователь правит, запись `write`
   * уходит с задержкой.
   */
  const commitPresetFilters = useCallback(
    (next: DashboardPresetFilters, write: () => Promise<boolean>) => {
      if (!presetId) return;
      // Новая смена перекрывает ещё не записанную
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = null;
      scheduledWriteRef.current = null;
      if (!isServerWrite) {
        void write();
        return;
      }
      setPending({ presetId, filters: next });
      scheduledWriteRef.current = () =>
        write().then(() => {
          // Записанное — или оставшееся прежним после отказа — показывает уже сам дашборд
          setPending((current) => (current?.filters === next ? null : current));
        });
      timerRef.current = setTimeout(() => void flushScheduledWrite(), SERVER_WRITE_DELAY_MS);
    },
    [presetId, isServerWrite, flushScheduledWrite],
  );

  const filters = useMemo<DashboardFiltersState>(
    () =>
      presetFilters
        ? {
            consultant: '',
            dateFrom: presetFilters.dateFrom,
            dateTo: presetFilters.dateTo,
            heatmapMode: standard.filters.heatmapMode,
          }
        : standard.filters,
    [presetFilters, standard.filters],
  );

  const activeAttributeFilters = presetFilters?.attributeFilters ?? standard.activeAttributeFilters;

  /** Консультанты фильтра по атрибуту; `undefined` — фильтра на панели нет (у стандартного — никогда) */
  const activeEmployeeIds = presetFilters?.employeeIds;

  /**
   * Фильтры, стоящие в панели: их получает новый дашборд. Консультант стандартного дашборда в него
   * не переходит — у пользовательского дашборда консультант добавляется фильтром по атрибуту.
   */
  const panelFilters = useMemo<DashboardPresetFilters>(
    () =>
      withEmployeeIds(
        { dateFrom: filters.dateFrom, dateTo: filters.dateTo, attributeFilters: activeAttributeFilters },
        activeEmployeeIds,
      ),
    [filters.dateFrom, filters.dateTo, activeAttributeFilters, activeEmployeeIds],
  );

  const { handleChangeFilters: changeStandardFilters } = standard;

  const handleChangeFilters = useCallback(
    (next: DashboardFiltersState) => {
      if (!presetFilters) {
        changeStandardFilters(next);
        return;
      }
      // Панель пользовательского дашборда меняет только период
      const { dateFrom, dateTo } = next;
      if (dateFrom === presetFilters.dateFrom && dateTo === presetFilters.dateTo) return;
      // Поля перечислены, а не скопированы: консультант, сохранённый в дашборде раньше отдельным выбором,
      // при записи отпадает
      const nextFilters = withEmployeeIds(
        { dateFrom, dateTo, attributeFilters: presetFilters.attributeFilters },
        presetFilters.employeeIds,
      );
      commitPresetFilters(nextFilters, () => setActiveFilters(nextFilters));
    },
    [presetFilters, changeStandardFilters, commitPresetFilters, setActiveFilters],
  );

  const { handleChangeAttributeFilters: changeStandardAttributeFilters } = standard;

  const handleChangeAttributeFilters = useCallback(
    (next: AttributeFilter[]) => {
      if (!presetFilters) {
        changeStandardAttributeFilters(next);
        return;
      }
      const nextFilters = withEmployeeIds(
        { dateFrom: presetFilters.dateFrom, dateTo: presetFilters.dateTo, attributeFilters: next },
        presetFilters.employeeIds,
      );
      commitPresetFilters(nextFilters, () => setActiveFilters(nextFilters));
    },
    [presetFilters, changeStandardAttributeFilters, commitPresetFilters, setActiveFilters],
  );

  /**
   * Консультанты фильтра по атрибуту — только у пользовательского дашборда; `undefined` убирает
   * фильтр с панели.
   */
  const handleChangeEmployeeIds = useCallback(
    (next: number[] | undefined) => {
      if (!presetFilters) return;
      const nextFilters = withEmployeeIds(
        {
          dateFrom: presetFilters.dateFrom,
          dateTo: presetFilters.dateTo,
          attributeFilters: presetFilters.attributeFilters,
        },
        next,
      );
      commitPresetFilters(nextFilters, () => setActiveFilters(nextFilters));
    },
    [presetFilters, commitPresetFilters, setActiveFilters],
  );

  const { handleResetFilters: resetStandardFilters } = standard;

  const handleResetFilters = useCallback(() => {
    if (!presetFilters) {
      resetStandardFilters();
      return;
    }
    const resetFilters = getDefaultPanelFilters();
    commitPresetFilters(resetFilters, () => resetActiveFilters(resetFilters));
    // Сброс — одно нажатие, ждать ему нечего
    void flushScheduledWrite();
  }, [presetFilters, resetStandardFilters, commitPresetFilters, resetActiveFilters, flushScheduledWrite]);

  return {
    filters,
    activeAttributeFilters,
    activeEmployeeIds,
    panelFilters,
    handleChangeFilters,
    handleChangeAttributeFilters,
    handleChangeEmployeeIds,
    handleResetFilters,
    /** Записывает ещё не записанную смену фильтров; обещание — до ответа сервера */
    flushPendingFilters: flushScheduledWrite,
  };
};
